import { formatDistanceToNow } from 'date-fns';
import {
  CheckCircleIcon,
  ClockCounterClockwiseIcon,
  ProhibitIcon,
  SpinnerIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { Num, Plural, T, useGT } from 'gt-react';
import type { AgentRunSummary, AgentRunTriggerKind } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import { cn } from 'src/lib/utils';
import {
  formatRunDuration,
  runDidNotStart,
  runWasSkipped,
} from '../agentPageRuns';
import type { RunHistoryFilter } from '../agentRunHistoryFilter';

function StatusIcon({
  status,
  notStarted,
}: {
  status: AgentRunSummary['status'];
  notStarted: boolean;
}) {
  if (status === 'running') {
    return (
      <SpinnerIcon className="size-4 shrink-0 animate-spin text-sky-600" />
    );
  }
  // A refused start is neither a green check nor the agent's own failure;
  // it still turns red when the gate failed it (resource held by a dead run).
  if (notStarted) {
    return (
      <ProhibitIcon
        weight="bold"
        className={cn(
          'size-4 shrink-0',
          status === 'failed' ? 'text-red-600' : 'text-stone-400',
        )}
      />
    );
  }
  if (status === 'completed') {
    return (
      <CheckCircleIcon
        weight="fill"
        className="size-4 shrink-0 text-emerald-600"
      />
    );
  }
  return <XCircleIcon weight="fill" className="size-4 shrink-0 text-red-600" />;
}

function useTriggerLabel() {
  const gt = useGT();
  return (kind: AgentRunTriggerKind | null | undefined): string | null => {
    switch (kind) {
      case 'manual':
        return gt('Started by hand', {
          context: 'how an agent run was started',
        });
      case 'schedule':
        return gt('Scheduled', { context: 'how an agent run was started' });
      case 'event':
        return gt('Triggered by an event', {
          context: 'how an agent run was started',
        });
      case 'api':
        return gt('Via API', { context: 'how an agent run was started' });
      case 'task_queue':
        return gt('Picked up from the queue', {
          context: 'how an agent run was started',
        });
      case 'workflow':
        return gt('From a workflow', {
          context: 'how an agent run was started',
        });
      default:
        return null;
    }
  };
}

function RunRow({
  run,
  selected,
  onSelect,
}: {
  run: AgentRunSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  const triggerLabel = useTriggerLabel()(run.triggerKind);
  const duration = formatRunDuration(run.duration);
  const startedAgo = formatDistanceToNow(new Date(run.startedAt), {
    addSuffix: true,
  });
  const notStarted = runDidNotStart(run);
  const skipped = runWasSkipped(run);
  const detail = run.status === 'failed' ? run.error : null;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className={cn(
          'flex w-full items-start gap-3 px-3 py-2 text-left transition-colors hover:bg-stone-50',
          selected && 'bg-stone-100 hover:bg-stone-100',
        )}
      >
        <span className="pt-0.5">
          <StatusIcon status={run.status} notStarted={notStarted} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[13px] font-medium text-stone-900">
              {startedAgo}
            </span>
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {run.status === 'running' ? (
                <T context="run history row, run still in progress">running</T>
              ) : (
                (duration ?? '—')
              )}
            </span>
          </span>
          <span className="block truncate text-[11.5px] text-muted-foreground">
            {detail ? (
              <span className="text-red-700">{detail}</span>
            ) : skipped ? (
              <T context="run history row: the run did not start because the agent was only waiting (start conditions not met or its resource busy)">
                Skipped
              </T>
            ) : notStarted ? (
              <T context="run history row, the start gate refused the run before it did any work">
                Did not start
              </T>
            ) : (
              triggerLabel
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

/**
 * The switch that hides skipped checks. Its label is the switch's accessible
 * name; the title explains what counts as skipped.
 */
function HideSkippedSwitch({
  filter,
  onFilterChange,
}: {
  filter: RunHistoryFilter;
  onFilterChange: (next: RunHistoryFilter) => void;
}) {
  const gt = useGT();
  return (
    <label
      className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground"
      title={gt(
        'Hide runs that did not start because the agent was only waiting: its start conditions were not met or a resource it needs was busy. Runs that could not start because of a problem, and runs started by hand, stay visible.',
        {
          context:
            'tooltip on the agent page history switch that hides skipped runs',
        },
      )}
    >
      <Switch
        checked={filter.hideSkipped}
        onCheckedChange={(hideSkipped) =>
          onFilterChange({ ...filter, hideSkipped })
        }
      />
      <T context="agent page history switch: hide runs that did not start because the agent was only waiting">
        Hide skipped
      </T>
    </label>
  );
}

/** Says how many skipped checks the filter left out, with a way to show them. */
function HiddenRunsNotice({
  hiddenCount,
  onShow,
}: {
  hiddenCount: number;
  onShow: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-[11.5px] text-muted-foreground">
      <span>
        <T context="agent page history footer: how many runs that did not start (agent was only waiting) are hidden">
          <Num>{hiddenCount}</Num> skipped{' '}
          <Plural n={hiddenCount} one="run" other="runs" /> hidden
        </T>
      </span>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-[11.5px]"
        onClick={onShow}
      >
        <T context="agent page history footer button: show the hidden skipped runs">
          Show them
        </T>
      </Button>
    </div>
  );
}

function EmptyHistory({ onlySkipped }: { onlySkipped: boolean }) {
  return (
    <p className="px-3 py-8 text-center text-sm text-muted-foreground">
      {onlySkipped ? (
        <T context="agent page run history empty state when every run so far was skipped and those are hidden">
          No runs have done any work yet.
        </T>
      ) : (
        <T context="agent page run history empty state">
          This agent has not run yet. Use Run to start it.
        </T>
      )}
    </p>
  );
}

/**
 * What the agent has done: its runs, newest first. Picking one opens it in the
 * detail card beside the list. By default skipped checks (runs the start gate
 * refused while the agent was only waiting) are left out; the header switch
 * and the footer bring them back.
 */
export function AgentRunHistory({
  runs,
  total,
  hiddenCount,
  filter,
  onFilterChange,
  selectedRunId,
  onSelectRun,
  onLoadMore,
  isLoadingMore,
}: {
  runs: AgentRunSummary[];
  /** Runs matching the filter, loaded or not. */
  total: number;
  /** Runs the filter left out; null when unknown. */
  hiddenCount: number | null;
  filter: RunHistoryFilter;
  onFilterChange: (next: RunHistoryFilter) => void;
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
  onLoadMore: () => void;
  isLoadingMore: boolean;
}) {
  const hidden = hiddenCount ?? 0;
  const showHiddenNotice = filter.hideSkipped && hidden > 0;
  return (
    <section className="flex max-h-[min(20rem,40vh)] min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-white lg:max-h-[min(36rem,60vh)]">
      <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <ClockCounterClockwiseIcon className="size-4 text-stone-500" />
          <T context="agent page run history heading">History</T>
        </h2>
        <span className="flex items-center gap-3">
          <HideSkippedSwitch filter={filter} onFilterChange={onFilterChange} />
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {total}
          </span>
        </span>
      </header>
      {runs.length === 0 ? (
        <EmptyHistory onlySkipped={showHiddenNotice} />
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
          {runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              selected={run.id === selectedRunId}
              onSelect={() => onSelectRun(run.id)}
            />
          ))}
        </ul>
      )}
      {runs.length < total && (
        <div className="border-t border-border p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            <T context="button to load older runs in the agent page history">
              Show older runs
            </T>
          </Button>
        </div>
      )}
      {showHiddenNotice && (
        <HiddenRunsNotice
          hiddenCount={hidden}
          onShow={() => onFilterChange({ ...filter, hideSkipped: false })}
        />
      )}
    </section>
  );
}
